'use client';
import React, { useState } from 'react';
import Badge from '@/components/ui/Badge';
import Icon from '@/components/ui/AppIcon';
import Toggle from '@/components/ui/Toggle';

const profiles = [
  {
    id: 'child-zara',
    name: 'Zara',
    age: 9,
    ageBand: '8–10',
    avatar: 'Z',
    avatarColor: 'bg-kid-dolphin/20 text-kid-dolphin',
    mode: 'Explorer',
    sessionMinutes: 47,
    active: true,
    quietOcean: false,
    teacherMode: false,
    badges: 3,
  },
  {
    id: 'child-leo',
    name: 'Leo',
    age: 6,
    ageBand: '5–7',
    avatar: 'L',
    avatarColor: 'bg-kid-plankton/20 text-kid-plankton',
    mode: 'Guardian',
    sessionMinutes: 0,
    active: false,
    quietOcean: true,
    teacherMode: false,
    badges: 1,
  },
];

export default function ChildProfileCards() {
  const [quietToggles, setQuietToggles] = useState<Record<string, boolean>>(
    Object.fromEntries(profiles.map((p) => [p.id, p.quietOcean]))
  );
  const [teacherToggles, setTeacherToggles] = useState<Record<string, boolean>>(
    Object.fromEntries(profiles.map((p) => [p.id, p.teacherMode]))
  );

  return (
    <div className="bg-card border border-border rounded-xl p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-foreground">Child Profiles</h2>
        <button className="flex items-center gap-1.5 text-xs text-primary hover:underline">
          <Icon name="PlusIcon" size={13} />
          Add Profile
        </button>
      </div>
      <div className="space-y-4">
        {profiles.map((profile) => (
          <div key={profile.id} className={`rounded-xl border p-4 transition-all ${profile.active ? 'border-kid-ocean/40 bg-kid-ocean/5' : 'border-border bg-muted/20'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${profile.avatarColor}`}>
                {profile.avatar}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-foreground">{profile.name}</p>
                  {profile.active && <Badge variant="positive" dot>Active</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">Age {profile.age} · Band {profile.ageBand} · {profile.mode} mode</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-foreground font-tabular">{profile.sessionMinutes}m</p>
                <p className="text-[10px] text-muted-foreground">today</p>
              </div>
            </div>

            <div className="space-y-2.5 pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-foreground">Quiet Ocean</p>
                  <p className="text-[10px] text-muted-foreground">Visual only — no audio</p>
                </div>
                <Toggle
                  size="sm"
                  checked={quietToggles[profile.id]}
                  onChange={(v) => setQuietToggles((prev) => ({ ...prev, [profile.id]: v }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-foreground">Teacher Mode</p>
                  <p className="text-[10px] text-muted-foreground">More data, non-distress</p>
                </div>
                <Toggle
                  size="sm"
                  checked={teacherToggles[profile.id]}
                  onChange={(v) => setTeacherToggles((prev) => ({ ...prev, [profile.id]: v }))}
                />
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-[10px] text-muted-foreground">{profile.badges} Guardian badges earned</span>
                <button className="text-[10px] text-primary hover:underline">Manage</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}